// src/components/CacheManagement.tsx
// キャッシュ管理UI

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress
} from '@mui/material';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorageIcon from '@mui/icons-material/Storage';
import { QuestionCacheService } from '@/services/questionCacheService';

interface CacheManagementProps {
  /** ダイアログとして表示するか */
  isDialog?: boolean;
  /** ダイアログを閉じる */
  onClose?: () => void;
}

const CacheManagement: React.FC<CacheManagementProps> = ({ isDialog = false, onClose }) => {
  const [cacheSize, setCacheSize] = useState<{ count: number; estimatedMB: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // キャッシュサイズを取得
  const loadCacheSize = async () => {
    setLoading(true);
    try {
      const size = await QuestionCacheService.getCacheSize();
      setCacheSize(size);
    } catch (error) {
      console.error('Failed to get cache size:', error);
      setMessage({ type: 'error', text: 'キャッシュサイズの取得に失敗しました' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCacheSize();
  }, []);

  // 古いキャッシュをクリア（30日以上前）
  const handleClearOldCache = async () => {
    setLoading(true);
    setMessage(null);
    try {
      await QuestionCacheService.clearOldCache();
      await loadCacheSize();
      setMessage({ type: 'success', text: '古いキャッシュをクリアしました' });
    } catch (error) {
      console.error('Failed to clear old cache:', error);
      setMessage({ type: 'error', text: '古いキャッシュのクリアに失敗しました' });
    } finally {
      setLoading(false);
    }
  };

  // 全キャッシュをクリア
  const handleClearAllCache = async () => {
    setLoading(true);
    setMessage(null);
    try {
      await QuestionCacheService.clearAllCache();
      await loadCacheSize();
      setMessage({ type: 'success', text: 'すべてのキャッシュをクリアしました' });
      setClearConfirmOpen(false);
    } catch (error) {
      console.error('Failed to clear all cache:', error);
      setMessage({ type: 'error', text: 'キャッシュのクリアに失敗しました' });
    } finally {
      setLoading(false);
    }
  };

  // キャッシュをリフレッシュ
  const handleRefreshCache = async () => {
    setMessage({ type: 'success', text: '次回アクセス時に最新データを取得します' });
  };

  const content = (
    <Box>
      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <StorageIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">キャッシュ情報</Typography>
          </Box>

          {loading && !cacheSize ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <Typography variant="body1" sx={{ mb: 1 }}>
                キャッシュ済み年度数: <strong>{cacheSize?.count || 0}</strong>
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                推定サイズ: <strong>{cacheSize?.estimatedMB || 0} MB</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                キャッシュされた問題は高速に読み込まれます。
                <br />
                キャッシュは7日間有効です。
              </Typography>
            </>
          )}
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefreshCache}
          disabled={loading}
          fullWidth
        >
          キャッシュを更新
        </Button>

        <Button
          variant="outlined"
          color="warning"
          startIcon={<DeleteSweepIcon />}
          onClick={handleClearOldCache}
          disabled={loading || !cacheSize || cacheSize.count === 0}
          fullWidth
        >
          古いキャッシュをクリア（30日以上前）
        </Button>

        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteSweepIcon />}
          onClick={() => setClearConfirmOpen(true)}
          disabled={loading || !cacheSize || cacheSize.count === 0}
          fullWidth
        >
          すべてのキャッシュをクリア
        </Button>
      </Box>

      {/* 確認ダイアログ */}
      <Dialog open={clearConfirmOpen} onClose={() => setClearConfirmOpen(false)}>
        <DialogTitle>すべてのキャッシュをクリアしますか？</DialogTitle>
        <DialogContent>
          <Typography>
            すべてのキャッシュをクリアすると、次回アクセス時に再度データをダウンロードする必要があります。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearConfirmOpen(false)}>キャンセル</Button>
          <Button onClick={handleClearAllCache} color="error" disabled={loading}>
            クリア
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  // ダイアログとして表示
  if (isDialog) {
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>キャッシュ管理</DialogTitle>
        <DialogContent>{content}</DialogContent>
        <DialogActions>
          <Button onClick={onClose}>閉じる</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // 通常のコンポーネントとして表示
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        キャッシュ管理
      </Typography>
      {content}
    </Box>
  );
};

export default CacheManagement;
